import React, { Suspense, useState } from 'react';
import { IOrder } from '@/interfaces/order';
import { Button, Checkbox, Divider, GetProp } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import PrintModal from '@/components/templates/print-modal';
import { printTemplates } from '@/configs/templates';
import Card from '@/components/card';
import Title from '@/components/title';
import { isObServe, isWareHouse } from '@/utils/permissionUtils';

const _modal: IDataModal<IOrder> = {
  open: false,
};

interface IProps {
  data: IOrder;
  afterPrint: () => void;
}

const OrderPrint = ({ data, afterPrint }: IProps) => {
  const isWarehouseRole = isWareHouse();
  const [print, setPrint] = useState<IDataModal<IOrder>>({ ..._modal });
  const [templates, setTemplates] = useState<string[]>(
    isWarehouseRole
      ? (data?.templates && data?.templates?.split(',')) || []
      : (data?.customer?.billingTemplates &&
          data?.customer?.billingTemplates?.split(',')) ||
          [],
  );

  const onChange: GetProp<
    typeof Checkbox.Group,
    'onChange'
  > = checkedValues => {
    if (checkedValues.includes('TM16') && !checkedValues.includes('TM12')) {
      checkedValues = [...checkedValues, 'TM12'];
    }
    setTemplates(checkedValues as string[]);
  };

  const isWareHouseTM12 = isWarehouseRole && !data?.isConfirm;

  return (
    <>
      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`รายการพิมพ์เอกสาร`}
      >
        <div className="flex justify-end items-end gap-2">
          {(data.isConfirm || !isWareHouse()) && !isObServe() && (
            <div className="text-gray-500">{`พิมพ์ ${data?.printCount} ครั้ง`}</div>
          )}
          {data.isConfirm || !isWareHouse() ? (
            <Button
              type="primary"
              disabled={!templates.length || isObServe()}
              onClick={() => {
                setPrint({
                  item: data,
                  open: true,
                  id: 'TM_ALL',
                });
              }}
            >
              <PrinterOutlined />
              {`พิมพ์เอกสารทั้งหมด  ${templates.length} รายการ`}
            </Button>
          ) : (
            <Button
              type="primary"
              ghost
              onClick={() => {
                setPrint({
                  item: data,
                  open: true,
                  id: 'TM12',
                });
              }}
            >
              <PrinterOutlined />
              {`พิมพ์ใบเบิกสินค้า`}
            </Button>
          )}
        </div>
      </Title>
      <Divider className="m-0" />
      <Checkbox.Group
        style={{ width: '100%' }}
        onChange={onChange}
        defaultValue={templates}
        disabled={!!isWarehouseRole}
      >
        <div className="grid md:grid-cols-4 w-full gap-4 h-full">
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งของ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM01', 'TM02', 'TM03'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบแจ้งหนี้"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM04', 'TM05', 'TM05-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับภาษี"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM06', 'TM07', 'TM07-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM08', 'TM09'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบเสร็จรับเงิน"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM10', 'TM11', 'TM11-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t =>
                ['TM12', 'TM13', 'TM14', 'TM15', 'TM16'].includes(t.key),
              )
              .map(p => (
                <Checkbox
                  className="flex"
                  key={p.key}
                  value={p.key}
                  disabled={['TM16'].includes(p.key)}
                >
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="อื่นๆ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM17', 'TM18', 'TM19', 'TM31'].includes(t.key))
              .map(p => (
                <Checkbox
                  className="flex"
                  key={p.key}
                  value={p.key}
                  disabled={['TM17', 'TM18'].includes(p.key)}
                >
                  {p.name}
                </Checkbox>
              ))}
          </Card>
        </div>
      </Checkbox.Group>

      <Suspense>
        <PrintModal
          open={print.open}
          handleCancel={function (): void {
            setPrint({
              ..._modal,
            });
          }}
          afterPrint={() => {
            setPrint({
              ..._modal,
            });
            afterPrint();
          }}
          order={data}
          tms={!isWareHouseTM12 ? templates : ['TM12']}
        />
      </Suspense>
    </>
  );
};

export default OrderPrint;
